'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import { reduxLogger } from '@/core/utils/logger';
import { BRAND_BLUE } from '@voley/shared-types/constants';

interface Props {
  children: ReactNode;
  stepName?: string;
  onRetry?: () => void;
  fallbackRoute?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary for Onboarding Flow
 *
 * Catches JavaScript errors anywhere in the onboarding flow and displays
 * a fallback UI with recovery options instead of crashing the whole app.
 *
 * Features:
 * - Logs errors with full context for debugging
 * - Provides "Try Again" and "Back to Login" recovery options
 * - Shows error details in development mode
 * - Styled consistently with the onboarding design system
 */
export class OnboardingErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error with full context
    reduxLogger.error('[OnboardingErrorBoundary] Uncaught error:', {
      error: error.message,
      stack: error.stack,
      step: this.props.stepName,
      componentStack: errorInfo.componentStack,
    });

    this.setState({ errorInfo });

    // In production, you might want to send this to an error tracking service
    // e.g., Sentry, LogRocket, etc.
  }

  handleRetry = () => {
    // Clear the error state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Call the optional onRetry callback
    this.props.onRetry?.();

    // Force a re-render by refreshing the page
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, stepName, fallbackRoute = '/login' } = this.props;

    if (!hasError) {
      return children;
    }

    const brandBlue = BRAND_BLUE;

    return (
      <div
        className="min-h-screen flex items-center justify-center p-4 bg-card-background"
      >
        {/* Subtle gradient background */}
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at top right, rgba(53, 129, 207, 0.04) 0%, transparent 50%), radial-gradient(ellipse at bottom left, rgba(53, 129, 207, 0.03) 0%, transparent 50%)',
          }}
        />

        <div className="max-w-md w-full text-center relative z-10">
          {/* Error Icon */}
          <div
            className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6"
            style={{ backgroundColor: 'var(--color-error-light)' }}
          >
            <AlertCircle className="h-8 w-8" style={{ color: 'var(--color-error)' }} />
          </div>

          {/* Title */}
          <h2 className="text-xl font-semibold mb-2 text-text-primary">
            {stepName ? `Error in ${stepName}` : 'Something went wrong'}
          </h2>

          {/* Description */}
          <p className="mb-6 text-text-tertiary">
            We encountered an unexpected error. Please try again or contact support if the problem
            persists.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <button
              onClick={this.handleRetry}
              className="px-6 py-2.5 rounded-full font-medium text-white inline-flex items-center justify-center gap-2 transition-all duration-200"
              style={{
                backgroundColor: brandBlue,
                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgb(0 0 0 / 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 1px 2px 0 rgb(0 0 0 / 0.05)';
              }}
            >
              <RefreshCw className="h-4 w-4" />
              Try Again
            </button>

            <Link
              href={fallbackRoute}
              className="px-6 py-2.5 rounded-full font-medium inline-flex items-center justify-center gap-2 transition-all duration-200"
              style={{
                backgroundColor: 'var(--color-hover-background)',
                color: 'var(--color-text-primary)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-border)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-hover-background)';
              }}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Login
            </Link>
          </div>

          {/* Error Details (Development Only) */}
          {process.env.NODE_ENV === 'development' && error && (
            <details className="mt-8 text-left p-4 rounded-lg bg-gray-50 border border-gray-200">
              <summary className="cursor-pointer font-medium text-sm text-gray-700 select-none">
                Error Details (Development Only)
              </summary>
              <div className="mt-3 space-y-3">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    Error Message
                  </p>
                  <pre className="text-sm overflow-auto whitespace-pre-wrap text-red-600 bg-red-50 p-2 rounded">
                    {error.message}
                  </pre>
                </div>

                {error.stack && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                      Stack Trace
                    </p>
                    <pre className="text-xs overflow-auto whitespace-pre-wrap text-gray-600 bg-gray-100 p-2 rounded max-h-48">
                      {error.stack}
                    </pre>
                  </div>
                )}

                {errorInfo?.componentStack && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                      Component Stack
                    </p>
                    <pre className="text-xs overflow-auto whitespace-pre-wrap text-gray-600 bg-gray-100 p-2 rounded max-h-48">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            </details>
          )}

          {/* Help Text */}
          <p className="mt-6 text-sm text-text-tertiary">
            If this problem persists, please contact{' '}
            <a
              href="mailto:support@voley.us"
              className="underline hover:no-underline"
              style={{ color: brandBlue }}
            >
              support@voley.us
            </a>
          </p>
        </div>
      </div>
    );
  }
}

export default OnboardingErrorBoundary;
